import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Check, LucideIcon } from "lucide-react";
import { motion } from "framer-motion";

interface PillarDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pillar: {
    icon: LucideIcon;
    number: string;
    title: string;
    subtitle: string;
    description: string;
    features: string[];
    outcomes: string[];
  } | null;
}

export function PillarDetailDialog({ open, onOpenChange, pillar }: PillarDetailDialogProps) {
  if (!pillar) return null;

  const Icon = pillar.icon;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px] !p-0 bg-card border-border/60">
        {/* Header with gradient */}
        <div className="relative px-5 sm:px-6 pt-6 sm:pt-8 pb-5 sm:pb-6 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent">
          <div className="absolute top-4 right-4">
            <span className="text-[50px] sm:text-[60px] font-bold text-primary/[0.08] leading-none select-none">
              {pillar.number}
            </span>
          </div>
          <DialogHeader className="relative z-10">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-11 sm:w-12 h-11 sm:h-12 rounded-xl bg-primary/15 flex items-center justify-center">
                <Icon className="w-5 sm:w-6 h-5 sm:h-6 text-primary" />
              </div>
              <div>
                <span className="text-[10px] sm:text-xs font-bold text-primary uppercase tracking-wider">
                  Pillar {pillar.number}
                </span>
                <DialogTitle className="text-lg sm:text-xl font-bold text-foreground">
                  {pillar.title}
                </DialogTitle>
              </div>
            </div>
            <DialogDescription className="text-sm sm:text-base font-semibold text-foreground/90 leading-relaxed">
              {pillar.subtitle}
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Content */}
        <div className="px-5 sm:px-6 py-5 sm:py-6">
          <p className="text-muted-foreground mb-5 sm:mb-6 leading-relaxed text-sm">
            {pillar.description}
          </p>

          <div className="grid sm:grid-cols-2 gap-5 sm:gap-6">
            {/* What We Build */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <h4 className="text-xs font-bold text-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
                <span className="w-6 h-[2px] bg-primary rounded-full" />
                What We Build
              </h4>
              <ul className="space-y-2">
                {pillar.features.map((feature, idx) => (
                  <motion.li
                    key={idx}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.15 + idx * 0.05 }}
                    className="flex items-start gap-2 text-xs sm:text-sm text-muted-foreground"
                  >
                    <span className="w-1 h-1 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                    <span className="leading-relaxed">{feature}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            {/* Outcomes */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h4 className="text-xs font-bold text-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
                <span className="w-6 h-[2px] bg-primary rounded-full" />
                Your Outcomes
              </h4>
              <ul className="space-y-2">
                {pillar.outcomes.map((outcome, idx) => (
                  <motion.li
                    key={idx}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.25 + idx * 0.05 }}
                    className="flex items-start gap-2 text-xs sm:text-sm"
                  >
                    <Check className="w-3.5 h-3.5 text-primary flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                    <span className="text-foreground leading-relaxed">{outcome}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
